import { DEV } from 'cc/env';
import { Component, Director, director, Scene, v3, Vec3, _decorator, Enum } from 'cc';
import globalInstance from './System/GlobalInstance';
import { loadCRM } from './BonusLauncher';
import { Orientation } from './OrientationManager';

const { ccclass, executionOrder, property } = _decorator;

let useCRMScenes: CRMSceneConfig[] = [];
let currentCRMSceneConfig: CRMSceneConfig = null;

const DEAULT_ROOT_PATH = 'Canvas/GameView/CRM';

async function _onSceneLaunched(scene: Scene) {
    if (!globalInstance.game.config.crm || globalInstance.game.config.isFakeToken) {
        return;
    }
    setCurrentCRMSceneConfig(scene.name);
    if (currentCRMSceneConfig) {
        loadCRM();
    }
}

export function setCurrentCRMSceneConfig(sceneName: string) {
    currentCRMSceneConfig = useCRMScenes.find((config) => config.sceneName === sceneName);
}

export function getCurrentCRMSceneConfig() {
    return currentCRMSceneConfig;
}

export enum PrefabModuleName {
    FloatingButton = 'FloatingButton',
    BackpackPage = 'BackpackPage',
    BulletinPage = 'BulletinPage',
    LoadingPage = 'LoadingPage',
    LeaderboardPage = 'LeaderboardPage',
    EffectRoot = 'EffectRoot',
    ToastifyManager = 'ToastifyManager',
    DropNotificationManager = 'DropNotificationManager',
    MessageManager = 'MessageManager',
    // CardCollectionPage = 'CardCollectionPage',
}

export type CRMPrefabConfigType = {
    name: PrefabModuleName;
    path: string;
    zIndex: number;
    landscapeInitPosition: Vec3;
    portraitInitPosition: Vec3;
};

@ccclass('CRMPrefabConfig')
class CRMPrefabConfig {
    @property({ type: Enum(PrefabModuleName), displayName: DEV && 'prefab 名稱' })
    protected name: PrefabModuleName = PrefabModuleName.ToastifyManager;

    @property({ displayName: DEV && '是否自訂路徑' })
    protected isCustomPath: boolean = false;

    @property({
        displayName: DEV && '路徑',
        visible(this: CRMPrefabConfig) {
            return this.isCustomPath;
        },
    })
    protected path: string = DEAULT_ROOT_PATH;

    @property({ displayName: DEV && '是否自訂 zIndex' })
    protected isCustomZIndex: boolean = false;

    @property({
        displayName: DEV && 'zindex',
        visible(this: CRMPrefabConfig) {
            return this.isCustomZIndex;
        },
    })
    protected zIndex: number = 0;

    @property({ displayName: DEV && '是否自訂 直橫位置' })
    protected isCustomPosition: boolean = false;

    @property({
        displayName: DEV && '橫式初始座標',
        visible(this: CRMPrefabConfig) {
            return this.isCustomPosition;
        },
    })
    protected landscapeInitPosition: Vec3 = v3();
    @property({
        displayName: DEV && '直式初始座標',
        visible(this: CRMPrefabConfig) {
            return this.isCustomPosition;
        },
    })
    protected portraitInitPosition: Vec3 = v3();

    public getConfig(): Partial<CRMPrefabConfigType> {
        return {
            name: this.name,
            ...(this.isCustomPath && { path: this.path }),
            ...(this.isCustomZIndex && { zIndex: this.zIndex }),
            ...(this.isCustomPosition && {
                landscapeInitPosition: this.landscapeInitPosition,
                portraitInitPosition: this.portraitInitPosition,
            }),
        };
    }
}

function createDefaultPrefabConfigs(rootPath: string): Record<PrefabModuleName, CRMPrefabConfigType> {
    const zIndexMap: Record<PrefabModuleName, number> = {
        [PrefabModuleName.MessageManager]: 0,
        [PrefabModuleName.FloatingButton]: 100,
        [PrefabModuleName.DropNotificationManager]: 150,
        [PrefabModuleName.LeaderboardPage]: 200,
        [PrefabModuleName.BackpackPage]: 300,
        // [PrefabModuleName.CardCollectionPage]: 350,
        [PrefabModuleName.BulletinPage]: 400,
        [PrefabModuleName.EffectRoot]: 500,
        [PrefabModuleName.ToastifyManager]: 900,
        [PrefabModuleName.LoadingPage]: 1000,
    };
    const landscapeInitPosition: Partial<Record<PrefabModuleName, Vec3>> = {
        [PrefabModuleName.FloatingButton]: v3(580, 350),
        [PrefabModuleName.ToastifyManager]: v3(0, -100),
    };

    const portraitInitPosition: Partial<Record<PrefabModuleName, Vec3>> = {
        [PrefabModuleName.FloatingButton]: v3(580, 350),
        [PrefabModuleName.ToastifyManager]: v3(300, 580),
    };

    const result: Record<PrefabModuleName, CRMPrefabConfigType> = {} as any;

    Object.keys(PrefabModuleName).forEach((name: PrefabModuleName) => {
        result[name] = {
            name,
            path: `${rootPath}`,
            zIndex: zIndexMap[name],
            landscapeInitPosition: landscapeInitPosition[name] ? landscapeInitPosition[name] : null,
            portraitInitPosition: portraitInitPosition[name] ? portraitInitPosition[name] : null,
        };
    });
    return result;
}

@ccclass('CRMSceneConfig')
class CRMSceneConfig {
    @property({ displayName: DEV && 'CRM 模組場景名稱' })
    public readonly sceneName: string = 'Game';
    @property({ displayName: DEV && 'CRM 模組圖層路徑' })
    public readonly rootPath: string = DEAULT_ROOT_PATH;

    @property({ displayName: DEV && '是否鎖死CRM模組方向' })
    public readonly isFixedOrientation: boolean = false;
    @property({
        type: Enum(Orientation),
        displayName: DEV && '鎖死方向',
        visible(this: CRMSceneConfig) {
            return this.isFixedOrientation;
        },
    })
    public readonly fixedOrientation: Orientation = Orientation.Landscape;

    @property({ displayName: DEV && '是否自定義CRM模組的prefab' })
    public readonly isCustomSettingPrefab: boolean = false;
    @property({
        type: [CRMPrefabConfig],
        displayName: DEV && 'CRM 模組 prefab 配置(複寫預設配置)',
        visible(this: CRMSceneConfig) {
            return this.isCustomSettingPrefab;
        },
    })
    private readonly customPrefabs: CRMPrefabConfig[] = [];
    private _defaultCRMPrefabConfigs: Record<PrefabModuleName, CRMPrefabConfigType> = null;
    private _overrideCrmPrefabConfigs: Partial<Record<PrefabModuleName, Partial<CRMPrefabConfigType>>> = {};

    /**
     * 覆寫 CRM 模組 prefab 配置
     * @param value - 要覆寫的配置
     */
    public set overrideCrmPrefabConfigs(value: Partial<Record<PrefabModuleName, Partial<CRMPrefabConfigType>>>) {
        this._overrideCrmPrefabConfigs = value;
    }

    public loadDefaultPrefabConfig() {
        this._defaultCRMPrefabConfigs = createDefaultPrefabConfigs(this.rootPath);
    }

    /**
     * CRM 模組 prefab 配置
     * 專案設定 > override 設置 > 初始設置
     */
    public getPrefabsConfigs(): Record<PrefabModuleName, CRMPrefabConfigType> {
        const configs: Record<PrefabModuleName, CRMPrefabConfigType> = { ...this._defaultCRMPrefabConfigs };
        Object.entries(this._overrideCrmPrefabConfigs).forEach((overrideCrmPrefabConfig) => {
            const configName = overrideCrmPrefabConfig[0];
            const overrideConfig = overrideCrmPrefabConfig[1];
            configs[configName] = {
                ...configs[configName],
                ...overrideConfig,
            };
        });
        Object.values(this.customPrefabs).forEach((customConfig) => {
            const config = customConfig.getConfig();
            if (this.isCustomSettingPrefab) {
                configs[config.name] = { ...configs[config.name], ...config };
            }
        });
        return configs;
    }
}

enum EventType {
    INIT_BONUS_MODULE = 'INIT_BONUS_MODULE',
    INIT_BONUS_MODULE_FINISHED = 'INIT_BONUS_MODULE_FINISHED',
    INIT_SCENE = 'INIT_SCENE',
}

@ccclass('BaseGameLauncher')
@executionOrder(-1)
export default abstract class BaseGameLauncher extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property({ tooltip: DEV && '要使用 CRM 的場景列表', type: [CRMSceneConfig] })
    protected readonly useCRMScenes: CRMSceneConfig[] = [];

    protected onLoad(): void {
        this.useCRMScenes.forEach((scene) => scene.loadDefaultPrefabConfig());
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected __preload() {
        ({ useCRMScenes } = this);
        this.initSceneLaunchEvent();
        this.launchGame();
    }

    protected initSceneLaunchEvent() {
        director.on(Director.EVENT_AFTER_SCENE_LAUNCH, _onSceneLaunched);
    }

    protected abstract launchGame();
}
